import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";

const TOL = 1e-9;

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

function interval(left, right, leftClosed = false, rightClosed = false) {
  return { left, right, leftClosed, rightClosed };
}

function eqNumber(a, b) {
  return (
    (a === Infinity && b === Infinity) ||
    (a === -Infinity && b === -Infinity) ||
    (Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) <= TOL)
  );
}

function sameInterval(a, b) {
  return (
    eqNumber(a.left, b.left) &&
    eqNumber(a.right, b.right) &&
    !!a.leftClosed === !!b.leftClosed &&
    !!a.rightClosed === !!b.rightClosed
  );
}

function sameIntervals(parsed, expected) {
  if (!parsed || parsed.kind !== "intervals") return false;
  const got = parsed.intervals || [];
  if (got.length !== expected.length) return false;
  return expected.every((interval, index) => sameInterval(got[index], interval));
}

function intervalRaw(interval) {
  const left = interval.leftClosed ? "[" : "(";
  const right = interval.rightClosed ? "]" : ")";
  return `${left}${interval.left}; ${interval.right}${right}`;
}

function intervalsRaw(intervals) {
  return intervals.map(intervalRaw).join(" ∪ ");
}

function intervalsLatex(intervals) {
  return intervalToLatex(intervalsRaw(intervals));
}

function rangeNonnegativeOnlyCase() {
  const right = randInt(4, 14);
  const leftClosed = Math.random() < 0.75;
  return {
    source: [interval(0, right, leftClosed, false)],
    negativePart: null,
    nonnegativePart: [interval(0, right, leftClosed, false)],
    reflectedPart: null,
    result: [interval(0, right, leftClosed, false)],
  };
}

function rangeCrossingZeroCase() {
  const leftAbs = randInt(6, 14);
  const right = randInt(2, Math.min(8, leftAbs - 1));
  return {
    source: [interval(-leftAbs, right)],
    negativePart: [interval(-leftAbs, 0)],
    nonnegativePart: [interval(0, right, true, false)],
    reflectedPart: [interval(0, leftAbs)],
    result: [interval(0, leftAbs, true, false)],
  };
}

function rangeNegativeAndPositiveMergingCase() {
  const nearZero = randInt(1, 4);
  const farNegativeAbs = randInt(nearZero + 5, nearZero + 13);
  const positiveLeft = randInt(nearZero + 1, farNegativeAbs - 2);
  const positiveRight = randInt(farNegativeAbs + 1, farNegativeAbs + 10);
  return {
    source: [
      interval(-farNegativeAbs, -nearZero, true, false),
      interval(positiveLeft, positiveRight),
    ],
    negativePart: [interval(-farNegativeAbs, -nearZero, true, false)],
    nonnegativePart: [interval(positiveLeft, positiveRight)],
    reflectedPart: [interval(nearZero, farNegativeAbs, false, true)],
    result: [interval(nearZero, positiveRight)],
  };
}

function rangeNegativeHiddenInPositiveCase() {
  const nearZero = randInt(1, 4);
  const farNegativeAbs = randInt(nearZero + 2, nearZero + 7);
  const positiveLeft = randInt(nearZero + 1, farNegativeAbs - 1);
  const positiveRight = randInt(farNegativeAbs + 4, farNegativeAbs + 14);
  return {
    source: [
      interval(-farNegativeAbs, -nearZero),
      interval(positiveLeft, positiveRight),
    ],
    negativePart: [interval(-farNegativeAbs, -nearZero)],
    nonnegativePart: [interval(positiveLeft, positiveRight)],
    reflectedPart: [interval(nearZero, farNegativeAbs)],
    result: [interval(nearZero, positiveRight)],
  };
}

function domainCrossingZeroCase() {
  const leftAbs = randInt(2, 8);
  const right = randInt(3, 12);
  return {
    source: [interval(-leftAbs, right)],
    nonnegativePart: [interval(0, right, true, false)],
    result: [interval(-right, right)],
  };
}

function domainPositiveWithoutZeroCase() {
  const right = randInt(4, 13);
  return {
    source: [interval(0, right)],
    nonnegativePart: [interval(0, right)],
    result: [interval(-right, 0), interval(0, right)],
  };
}

function domainPositiveIntervalCase() {
  const positiveLeft = randInt(1, 6);
  const positiveRight = randInt(positiveLeft + 3, positiveLeft + 10);
  const negativeLeft = -randInt(positiveRight + 2, positiveRight + 10);
  const negativeRight = -randInt(positiveRight + 1, positiveRight + 1);
  return {
    source: [
      interval(negativeLeft, negativeRight),
      interval(positiveLeft, positiveRight),
    ],
    nonnegativePart: [interval(positiveLeft, positiveRight)],
    result: [interval(-positiveRight, -positiveLeft), interval(positiveLeft, positiveRight)],
  };
}

function domainZeroPartAndRayCase() {
  const middleRight = randInt(1, 5);
  const rayLeft = randInt(middleRight + 2, middleRight + 7);
  const negativeLeft = -randInt(2, 8);
  return {
    source: [
      interval(negativeLeft, middleRight, true, false),
      interval(rayLeft, Infinity),
    ],
    nonnegativePart: [
      interval(0, middleRight, true, false),
      interval(rayLeft, Infinity),
    ],
    result: [
      interval(-Infinity, -rayLeft),
      interval(-middleRight, middleRight),
      interval(rayLeft, Infinity),
    ],
  };
}

const RANGE_CASE_GENERATORS = [
  rangeNonnegativeOnlyCase,
  rangeCrossingZeroCase,
  rangeNegativeAndPositiveMergingCase,
  rangeNegativeHiddenInPositiveCase,
];

const DOMAIN_CASE_GENERATORS = [
  domainCrossingZeroCase,
  domainPositiveWithoutZeroCase,
  domainPositiveIntervalCase,
  domainZeroPartAndRayCase,
];

export function generateTask(kind) {
  const type = kind || pick(["rangeAbsY", "domainAbsX"]);
  const taskCase = (type === "rangeAbsY" ? pick(RANGE_CASE_GENERATORS) : pick(DOMAIN_CASE_GENERATORS))();
  return {
    type: "AbsoluteValueSetTransformTask",
    data: { transformKind: type, ...taskCase },
  };
}

export function renderTask({ data }) {
  if (data.transformKind === "rangeAbsY") {
    return (
      <div style={{ lineHeight: 1.6 }}>
        Zbiorem wartości funkcji <InlineMath math={"y=f(x)"} /> jest{" "}
        <InlineMath math={`ZW=${intervalsLatex(data.source)}`} />. Wyznacz zbiór wartości funkcji{" "}
        <InlineMath math={"y=|f(x)|"} />.
      </div>
    );
  }

  return (
    <div style={{ lineHeight: 1.6 }}>
      Dziedziną funkcji <InlineMath math={"y=f(x)"} /> jest{" "}
      <InlineMath math={`D=${intervalsLatex(data.source)}`} />. Wyznacz dziedzinę funkcji{" "}
      <InlineMath math={"y=f(|x|)"} />.
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const label = task?.data?.transformKind === "rangeAbsY" ? "ZW" : "D";
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ marginBottom: 4 }}>
        <InlineMath math={`${label}=`} />
      </div>
      <IntervalInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        asString
        showPreview
        placeholder="np. [0; 5)  albo  (-5; -2) ∪ (2; 5)"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const parsed = typeof userAnswer === "string" ? parseIntervalInput(userAnswer) : userAnswer?.parsed;
  return sameIntervals(parsed, data.result);
}

export function renderAnswer({ data }) {
  const label = data.transformKind === "rangeAbsY" ? "ZW" : "D";
  return <InlineMath math={`${label}=${intervalsLatex(data.result)}`} />;
}

export function renderUserAnswer(arg1, arg2) {
  const task = arg1?.type === "AbsoluteValueSetTransformTask" ? arg1 : arg2;
  const userAnswer = arg1?.type === "AbsoluteValueSetTransformTask" ? arg2 : arg1;
  if (!userAnswer || (typeof userAnswer === "object" && !userAnswer.raw)) return <i>(brak odpowiedzi)</i>;
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer.raw;
  const label = task?.data?.transformKind === "rangeAbsY" ? "ZW" : "D";
  return <InlineMath math={`${label}=${intervalToLatex(raw)}`} />;
}

export function renderExplanation({ data }) {
  if (data.transformKind === "rangeAbsY") {
    return (
      <div style={{ lineHeight: 1.7 }}>
        <p>
          W funkcji <InlineMath math={"y=|f(x)|"} /> wartości ujemne odbijają się nad oś{" "}
          <InlineMath math={"OX"} />, a wartości nieujemne zostają bez zmian.
        </p>
        <p>
          Dany zbiór wartości:
          <BlockMath math={`ZW_f=${intervalsLatex(data.source)}`} />
        </p>
        {data.negativePart ? (
          <p>
            Część ujemna <InlineMath math={intervalsLatex(data.negativePart)} /> po odbiciu daje{" "}
            <InlineMath math={intervalsLatex(data.reflectedPart)} />.
          </p>
        ) : (
          <p>W zbiorze wartości nie ma części ujemnej, więc nie ma czego odbijać.</p>
        )}
        <p>
          Część nieujemna zostaje:
          <BlockMath math={data.nonnegativePart ? intervalsLatex(data.nonnegativePart) : "\\varnothing"} />
        </p>
        <p>
          Bierzemy sumę tego, co zostało, i tego, co odbiło się nad oś:
          <BlockMath math={`ZW_{|f|}=${intervalsLatex(data.result)}`} />
        </p>
      </div>
    );
  }

  return (
    <div style={{ lineHeight: 1.7 }}>
      <p>
        W funkcji <InlineMath math={"y=f(|x|)"} /> do funkcji <InlineMath math={"f"} /> trafia tylko liczba{" "}
        <InlineMath math={"|x|"} />, czyli wartość nieujemna.
      </p>
      <p>
        Z dawnej dziedziny bierzemy więc tylko część nieujemną:
        <BlockMath math={`D_f=${intervalsLatex(data.source)}`} />
        <BlockMath math={`D_f\\cap[0,\\infty)=${intervalsLatex(data.nonnegativePart)}`} />
      </p>
      <p>
        Warunek <InlineMath math={"|x|\\in D_f"} /> oznacza, że każdą dodatnią część odbijamy symetrycznie na lewo, a
        punkt <InlineMath math={"0"} /> zostaje, jeśli należał do dziedziny.
      </p>
      <p>
        Otrzymujemy:
        <BlockMath math={`D_{f(|x|)}=${intervalsLatex(data.result)}`} />
      </p>
    </div>
  );
}
